import { useEffect, useState } from "react";
import { useUserStore } from "entities/user";
import { USER_ROLES } from "shared/config/routes";
import { myEventsRecordsRequest } from "pages/lk/api/clientEventsRequest";
import { EventCard } from "./EventCard";
import { Pagination } from "shared/ui/pagination";
import { RecordDeleteConfirmModal } from "./RecordDeleteConfirmModal";
import { RecordDeleteSuccessModal } from "./RecordDeleteSuccessModal";
import { QrCodeModal } from "./QrCodeModal";
import { useHttpRequest } from "shared/lib/hooks/useHttpRequest";
import { clientDeleteEventRecordRequest } from "pages/lk/api/clientDeleteEventRecordRequest";
import styles from "./ClientEvents.module.scss";

const PAGE_SIZE = 10;

export const ClientEvents = () => {
  const { role } = useUserStore();
  const [page, setPage] = useState(1);
  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
    total: 0,
    pages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleteSuccessOpen, setIsDeleteSuccessOpen] = useState(false);
  const [isQrCodeOpen, setIsQrCodeOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [refreshEventsKey, setRefreshEventsKey] = useState(0);

  const { sendRequest, requestLoading, requestError, clearError } =
    useHttpRequest(clientDeleteEventRecordRequest);

  const loadEvents = async (currentPage, signal) => {
    try {
      setLoading(true);
      setError(null);

      const data = await myEventsRecordsRequest(
        { page: currentPage, pageSize: PAGE_SIZE },
        { signal },
      );

      if (signal.aborted) return;

      setRecords(Array.isArray(data?.total) ? data.total : []);
      setPagination(
        data?.pagination ?? {
          page: currentPage,
          pageSize: PAGE_SIZE,
          total: 0,
          pages: 1,
        },
      );
    } catch (e) {
      if (e.name !== "AbortError" && !signal.aborted) {
        setError(e);
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  };

  const handlePageChange = (nextPage) => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    setPage(nextPage);
  };

  const openDeleteModal = (event) => {
    setSelectedEvent(event);
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (requestLoading || !selectedEvent?.id) return;
    try {
      await sendRequest(selectedEvent?.id);
      setIsDeleteConfirmOpen(false);
      setIsDeleteSuccessOpen(true);
    } catch (error) {
      const status = error?.status;

      if (status === 401) {
        return;
      }

      if (status >= 500) {
        return;
      }
    }
  };

  const handleSuccessModalClose = () => {
    setIsDeleteSuccessOpen(false);
    setSelectedEvent(null);
    setRefreshEventsKey((prev) => prev + 1);
  };

  const openQrCodeModal = (event) => {
    setSelectedEvent(event);
    setIsQrCodeOpen(true);
  };

  useEffect(() => {
    if (role !== USER_ROLES.CLIENT) return;

    const controller = new AbortController();

    loadEvents(page, controller.signal);

    return () => {
      controller.abort();
    };
  }, [role, page, refreshEventsKey]);

  if (role !== USER_ROLES.CLIENT) return null;

  return (
    <div className={styles.contentWrapper}>
      {loading && (
        <div className={styles.contentWrapper__stateBlock}>
          <p className={styles.contentWrapper__stateText}>Загрузка...</p>
        </div>
      )}
      {error && (
        <div className={styles.contentWrapper__stateBlock}>
          <p className={styles.contentWrapper__stateText}>Ошибка загрузки</p>
        </div>
      )}
      <div className={styles.contentWrapper__eventsBlock}>
        {!loading && !error && records.length === 0 && (
          <div className={styles.contentWrapper__stateBlock}>
            <p className={styles.contentWrapper__stateText}>
              У вас пока что нет регистрации на мероприятия
            </p>
          </div>
        )}

        {!loading &&
          !error &&
          records.map((item) => (
            <EventCard
              key={item.id}
              event={item}
              onDeleteClick={() => openDeleteModal(item)}
              onQrCodeClick={() => openQrCodeModal(item)}
            />
          ))}
      </div>
      <div className={styles.contentWrapper__paginationBlock}>
        <Pagination
          page={pagination.page}
          totalPages={pagination.pages}
          onPageChange={handlePageChange}
        />
      </div>
      <RecordDeleteConfirmModal
        isModalOpen={isDeleteConfirmOpen}
        close={() => setIsDeleteConfirmOpen(false)}
        toggle={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
      />

      <RecordDeleteSuccessModal
        isModalOpen={isDeleteSuccessOpen}
        close={handleSuccessModalClose}
        toggle={handleSuccessModalClose}
      />

      <QrCodeModal
        isModalOpen={isQrCodeOpen}
        close={() => setIsQrCodeOpen(false)}
        toggle={() => setIsQrCodeOpen(false)}
        event={selectedEvent}
      />
    </div>
  );
};
